import { wireIntegrationLocate } from '@/lib/notifications/bind-integration-locate.js';
import { useCollectionMethodStore } from '@/store/collection-method.store.js';
import { useLegalReasonStore } from '@/store/legal-reason.store.js';
import useProcessingPurposeStore from '@/store/processing-purpose.store.js';
import { useSubjectPersonGroupStore } from '@/store/subject-person-group.store.js';
import { useProcessStore } from '@/store/process.store.js';
import { useThreatStore } from '@/store/threat.store.js';
import { useRootCauseStore } from '@/store/root-cause.store.js';
import { useClassificationStore } from '@/store/classification.store.js';
import { useDocumentTypeStore } from '@/store/document-type.store.js';
import { useTeamStore } from '@/store/team.store.js';
import { useCompanyStore } from '@/store/company.store.js';
import { useApplicationMethodStore } from '@/store/application-method.store.js';
import { useApplicationRequestInformationStore } from '@/store/application-request-information.store.js';
import { useExplicitConsentMethodStore } from '@/store/explicit-consent-method.store.js';
import { useExplicitConsentReasonStore } from '@/store/explicit-consent-reason.store.js';
import { useMailTemplateStore } from '@/store/mail-template.store.js';
import useKVKKCategoryStore from '@/store/kvkk-category.store.js';
import {
    useRetentionPeriodTableStore,
    useTransferTypeTableStore,
    useRegulatorySourceTableStore,
    useRegulatoryClauseTableStore,
    useComplianceObligationTableStore,
    useComplianceFrameworkTableStore,
    useDpiaTemplateTableStore,
    useGovernanceReportTableStore,
    useInternationalTransferTableStore,
    useVendorAssessmentTableStore,
} from '@/store/simple-reference-table.store.js';

/**
 * Attach locateRowInList for legacy ReferenceDataTablePage integrations (attachReferenceTableContract).
 * @param {Record<string, object>} integrations
 */
export function wireReferenceTableLocate(integrations) {
    const specs = [
        [integrations.collectionMethodIntegration, useCollectionMethodStore, (s) => s.methods],
        [integrations.legalReasonIntegration, useLegalReasonStore, (s) => s.reasons],
        [integrations.processingPurposeIntegration, useProcessingPurposeStore, (s) => s.purposes],
        [integrations.subjectPersonGroupIntegration, useSubjectPersonGroupStore, (s) => s.groups],
        [integrations.processIntegration, useProcessStore, (s) => s.processes],
        [integrations.threatIntegration, useThreatStore, (s) => s.threats],
        [integrations.rootCauseIntegration, useRootCauseStore, (s) => s.rootCauses],
        [integrations.classificationIntegration, useClassificationStore, (s) => s.classificationItems],
        [integrations.documentTypeIntegration, useDocumentTypeStore, (s) => s.documentTypes],
        [integrations.teamIntegration, useTeamStore, (s) => s.teams],
        [integrations.companyIntegration, useCompanyStore, (s) => s.committeeMembers],
        [integrations.applicationMethodIntegration, useApplicationMethodStore, (s) => s.methods],
        [integrations.applicationRequestInformationIntegration, useApplicationRequestInformationStore, (s) => s.items],
        [integrations.explicitConsentMethodIntegration, useExplicitConsentMethodStore, (s) => s.methods],
        [integrations.explicitConsentReasonIntegration, useExplicitConsentReasonStore, (s) => s.reasons],
        [integrations.mailTemplateIntegration, useMailTemplateStore, (s) => s.mailTemplates],
        [integrations.personalDataCategoryIntegration, useKVKKCategoryStore, (s) => s.categories],
        [integrations.privacyRetentionTableIntegration, useRetentionPeriodTableStore, (s) => s.items],
        [integrations.transferTypeTableIntegration, useTransferTypeTableStore, (s) => s.items],
        [integrations.regulatorySourceIntegration, useRegulatorySourceTableStore, (s) => s.items],
        [integrations.regulatoryClauseIntegration, useRegulatoryClauseTableStore, (s) => s.items],
        [integrations.complianceObligationIntegration, useComplianceObligationTableStore, (s) => s.items],
        [integrations.complianceFrameworkIntegration, useComplianceFrameworkTableStore, (s) => s.items],
        [integrations.dpiaTemplateIntegration, useDpiaTemplateTableStore, (s) => s.items],
        [integrations.governanceReportDefinitionIntegration, useGovernanceReportTableStore, (s) => s.items],
        [integrations.internationalTransferTableIntegration, useInternationalTransferTableStore, (s) => s.items],
        [integrations.vendorAssessmentIntegration, useVendorAssessmentTableStore, (s) => s.items],
    ];

    for (const [integration, useStore, getItems] of specs) {
        if (!integration?.fetchList) continue;
        wireIntegrationLocate({
            integration,
            useStore,
            getItems,
            fetchList: (...args) => integration.fetchList(...args),
        });
    }
}
